import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { GridRenderCellParams, GridSortModel } from "@mui/x-data-grid-pro";
import { inarixColors } from "inarix-theme";
import { Chip, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

//Status icons
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NewReleasesTwoToneIcon from "@mui/icons-material/NewReleasesTwoTone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneIcon from "@mui/icons-material/Done";

import { useTranslation } from "react-i18next";
import { ColumnsForDataGridPro } from "../../../../redux/actions/dataTable/DataTableTypes";
import moment from "moment";
import {
  changeStatusColumn,
  localeData,
  setPinnedColumns,
  setPreselectedFilterField,
  setShowFilterForm,
  setSortModel,
} from "../../../../redux/slices/dataTable/data";
import {
  resetActionDetails,
  setSampleId,
} from "../../../../redux/slices/data/dataSampleView";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { displayCorrectValue } from "../../../data/dataSampleView/DataSampleRightSide";
import { ColumnStyles } from "./columnStyles";
import { selectDataTableUnifiedState } from "../../../../redux/selectors/unifiedSelectors";
import { dataTableActionNewFilter } from "../../../../redux/actions/dataTable/DataTableAsyncActions";

import CustomColumnHeader from "../customColumnHeader/CustomColumnHeader";
import { getOperatorsFromAvailableFilters } from "../../../helpers/helpersSearchs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    setIndividualSample: (pastaActionId: number, navigate: any) => {
      dispatch(resetActionDetails());
      dispatch(setSampleId(pastaActionId));
      if (pastaActionId) {
        navigate(`/data/${pastaActionId}`);
      }
    },

    toggleColumnVisibility: (columnField: string) => {
      dispatch(changeStatusColumn(columnField));
    },

    setPinnedColumns: (pinnedColumns: { left: string[]; right: string[] }) => {
      dispatch(setPinnedColumns(pinnedColumns));
    },

    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(true));
      dispatch(setPreselectedFilterField(field));
    },

    setSortModel: (sortModel: GridSortModel) => {
      dispatch(setSortModel(sortModel));
    },
  };
};

const DataTableColumns = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { onOpenFilter, setSortModel } = SetActions();
  const [t] = useTranslation("global");

  const { columns, actions, pinnedColumns, filterModel, sortModel } =
    useSelector(selectDataTableUnifiedState);

  const handleSort = (field: string, direction: "asc" | "desc" | null) => {
    if (direction === null) {
      setSortModel([]);
    } else {
      setSortModel([{ field, sort: direction }]);
    }

    dispatch(dataTableActionNewFilter());
  };

  const { setIndividualSample, toggleColumnVisibility, setPinnedColumns } =
    SetActions();

  const navigate = useNavigate();
  const locale = useSelector(localeData);

  let columnsDataTable: ColumnsForDataGridPro[] | undefined = columns?.map(
    (column) => {
      if (column.field === "userFacingDeletion") {
        return {
          field: column.field,
          headerName: t("custom_filter.userFacingDeletion"),
          width: 250,
          disableColumnMenu: true,
          sortable: false,
          type: "string",
          filterOperators: getOperatorsFromAvailableFilters(["$eq", "$ne"]),
          filter: "API",
          service: "userFacingDeletion",
          queryFormat: "userFacingDeletion=",
        };
      }
      if (column.field === "id") {
        return {
          headerName: t("Data.table.Details"),
          field: "id",
          width: 120,
          sortable: false,
          type: "actions",
          show: column.show,
          disableColumnMenu: true,
          columnType: column.columnType,
          useShortResult: column.useShortResult,
          foregroundColor: column.foregroundColor,
          backgroundColor: column.backgroundColor,

          renderHeader: () => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomColumnHeader
                filterModel={filterModel}
                sortModel={sortModel}
                headerName={t("Data.table.Details")}
                field={column.field}
                allColumns={columns}
                onToggleVisibility={toggleColumnVisibility}
                setPinnedColumns={setPinnedColumns}
                pinnedColumns={pinnedColumns}
                columnType={column.columnType}
                onOpenFilter={onOpenFilter}
                onSort={handleSort}
                sortable={false}
              />
            </div>
          ),
          renderCell: (params: GridRenderCellParams) => {
            const chipIdDevice = () => {
              const idConsole = () => {
                setIndividualSample(params.row.details as number, navigate);
              };
              return (
                <div
                  style={{
                    ...ColumnStyles.detailsContainer,
                    borderLeftColor: inarixColors.divider_inarix,
                    borderRightColor: inarixColors.divider_inarix,
                  }}
                >
                  <Tooltip
                    title={t("Fleet_actions.table.Details" as string)}
                    arrow
                  >
                    <IconButton
                      sx={{
                        ...ColumnStyles.detailsButton,
                        "&:hover": {
                          color: inarixColors.light_inarix,
                        },
                      }}
                      onClick={idConsole}
                      aria-label="delete"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              );
            };
            return chipIdDevice();
          },
        };
      }

      if (column.field === "cropTextId") {
        return {
          field: column.field,
          headerName: column.headerName,
          width: column.width,
          filterable:
            column.availableFilters && column.availableFilters.length > 0,
          filterOperators: getOperatorsFromAvailableFilters(
            column.availableFilters,
          ),

          sortable: false,
          show: column.show,
          disableColumnMenu: true,
          columnType: column.columnType,
          useShortResult: column.useShortResult,
          renderHeader: () => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomColumnHeader
                filterModel={filterModel}
                sortModel={sortModel}
                availableFilters={column.availableFilters}
                headerName={column.headerName}
                field={column.field}
                allColumns={columns}
                onToggleVisibility={toggleColumnVisibility}
                setPinnedColumns={setPinnedColumns}
                pinnedColumns={pinnedColumns}
                columnType={column.columnType}
                onOpenFilter={onOpenFilter}
                onSort={handleSort}
                sortable={false}
              />
            </div>
          ),
          renderCell: (params: GridRenderCellParams) => {
            const colors = params?.row?.colors;
            const backgroundColor = colors?.iconBackgroundHexColor;
            const foregroundColor = colors?.iconForegroundHexColor;
            const value = params.value;

            return (
              <div>
                <Chip
                  label={value ? value : ""}
                  sx={{
                    ...ColumnStyles.cropChip,
                    backgroundColor: backgroundColor,
                    color: foregroundColor,
                  }}
                />
              </div>
            );
          },
        };
      }

      if (column.columnType === "measure") {
        return {
          field: column.field,
          headerName: column.headerName,
          width: column.width,
          sortable: false,
          show: column.show,
          disableColumnMenu: true,
          filterable: false,
          columnType: column.columnType,
          height: 500,
          useShortResult: column.useShortResult,
          renderHeader: () => (
            <CustomColumnHeader
              filterModel={filterModel}
              sortModel={sortModel}
              headerName={column.headerName}
              field={column.field}
              allColumns={columns}
              onToggleVisibility={toggleColumnVisibility}
              setPinnedColumns={setPinnedColumns}
              pinnedColumns={pinnedColumns}
              columnType={column.columnType}
              onOpenFilter={onOpenFilter}
              onSort={handleSort}
              sortable={false}
            />
          ),
          renderCell: (params: GridRenderCellParams) => {
            const values = params.value;

            if (column.useShortResult) {
              return <div>{values.shortResult}</div>;
            } else {
              return (
                <div className="measure-container-data-table">
                  {values !== undefined
                    ? values?.detailedResult?.length > 0
                      ? values?.detailedResult?.map((v) => (
                          <div key={`${v?.title}`}>
                            <div className="">{v?.title}</div>
                            <div className="">{displayCorrectValue(v)}</div>
                          </div>
                        ))
                      : values.shortResult
                    : ""}
                </div>
              );
            }
          },
        };
      }

      if (column.field === "computedStatusId") {
        return {
          field: column.field,
          headerName: column.headerName,
          width: column.width,
          sortable: false,
          show: column.show,
          disableColumnMenu: true,
          filterable:
            column.availableFilters && column.availableFilters.length > 0,
          filterOperators: getOperatorsFromAvailableFilters(
            column.availableFilters ? column.availableFilters : [],
          ),
          columnType: column.columnType,
          useShortResult: column.useShortResult,
          foregroundColor: column.foregroundColor,
          backgroundColor: column.backgroundColor,
          renderCell: (params: GridRenderCellParams) => {
            const value = params.value;
            const deleted = 141;
            const abortedNoValidated = 140;
            const finishedWithErrors = 139;
            const finishedIncomplete = 138;
            const finishedComplete = 137;
            const inProgress = 136;

            const statusConfig = {
              [deleted]: {
                chipColor: "default" as const,
                label: t("Data.table.manage_status_columns.141"),
                styles: ColumnStyles.statusChips.default,
                icon: (
                  <DeleteOutlineIcon
                    sx={{ ...ColumnStyles.statusIcon, color: "#9e9e9e" }}
                  />
                ),
              },
              [abortedNoValidated]: {
                chipColor: "default" as const,
                label: t("Data.table.manage_status_columns.140"),
                styles: ColumnStyles.statusChips.default,
                icon: (
                  <NewReleasesTwoToneIcon
                    sx={{ ...ColumnStyles.statusIcon, color: "#9e9e9e" }}
                  />
                ),
              },
              [finishedWithErrors]: {
                chipColor: "error" as const,
                label: t("Data.table.manage_status_columns.139"),
                styles: ColumnStyles.statusChips.failed,
                icon: (
                  <DoneIcon
                    sx={{ ...ColumnStyles.statusIcon, color: "#d32f2f" }}
                  />
                ),
              },
              [finishedIncomplete]: {
                chipColor: "info" as const,
                label: t("Data.table.manage_status_columns.138"),
                styles: ColumnStyles.statusChips.processing,
                icon: (
                  <DoneIcon
                    sx={{ ...ColumnStyles.statusIcon, color: "#1976d2" }}
                  />
                ),
              },
              [finishedComplete]: {
                chipColor: "success" as const,
                label: t("Data.table.manage_status_columns.137"),
                styles: ColumnStyles.statusChips.completed,
                icon: (
                  <CheckCircleOutlineIcon
                    sx={{ ...ColumnStyles.statusIcon, color: "#2e7d32" }}
                  />
                ),
              },
              [inProgress]: {
                chipColor: "info" as const,
                label: t("Data.table.manage_status_columns.136"),
                styles: ColumnStyles.statusChips.processing,
                icon: (
                  <FontAwesomeIcon
                    icon={faClockRotateLeft}
                    fontSize="14px"
                    color="#1976d2"
                    style={{ marginRight: "4px" }}
                  />
                ),
              },
              default: {
                chipColor: "default" as const,
                label: t("Data.table.status_columns.default"),
                styles: ColumnStyles.statusChips.default,
                icon: <HelpOutlineIcon sx={ColumnStyles.statusIcon} />,
              },
            };

            const config =
              statusConfig[value as keyof typeof statusConfig] ||
              statusConfig.default;

            const tooltipDescription = () => {
              switch (value) {
                case deleted:
                  return t("Data.table.manage_status_columns_desc.141");
                case abortedNoValidated:
                  return t("Data.table.manage_status_columns_desc.140");
                case finishedWithErrors:
                  return t("Data.table.manage_status_columns_desc.139");
                case finishedIncomplete:
                  return t("Data.table.manage_status_columns_desc.138");
                case finishedComplete:
                  return t("Data.table.manage_status_columns_desc.137");
                case inProgress:
                  return (
                    <span>
                      {` ${t("Data.table.manage_status_columns_desc.136")}  `}
                      <span style={{ color: "#ffd54f", fontWeight: 600 }}>
                        {t("Data.table.manage_status_columns_desc.136desc")}
                      </span>
                    </span>
                  );

                default:
                  return t("Data.table.status_columns_desc.default");
              }
            };

            return (
              <div>
                <Tooltip title={tooltipDescription()} arrow>
                  <Chip
                    label={
                      <div style={ColumnStyles.chipContainer}>
                        {config.icon}
                        {config.label}
                      </div>
                    }
                    color={config.chipColor}
                    size="small"
                    variant="outlined"
                    sx={{
                      ...ColumnStyles.statusChip,
                      backgroundColor: config.styles.backgroundColor,
                      color: config.styles.color,
                      borderColor: config.styles.borderColor,
                    }}
                  />
                </Tooltip>
              </div>
            );
          },
          renderHeader: () => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomColumnHeader
                filterModel={filterModel}
                sortModel={sortModel}
                availableFilters={column.availableFilters}
                headerName={column.headerName}
                field={column.field}
                allColumns={columns}
                onToggleVisibility={toggleColumnVisibility}
                setPinnedColumns={setPinnedColumns}
                pinnedColumns={pinnedColumns}
                columnType={column.columnType}
                onOpenFilter={onOpenFilter}
                onSort={handleSort}
                sortable={false}
              />
            </div>
          ),
        };
      }
      return {
        field: column.field,
        headerName: column.headerName,
        width: column.width,
        sortable: false,
        show: column.show,
        disableColumnMenu: true,
        filterable:
          column.field === "createdAt"
            ? false
            : column.availableFilters && column.availableFilters.length > 0,
        filterOperators: getOperatorsFromAvailableFilters(
          column.columnType === "data" ||
            column.columnType === "exported-measure"
            ? column.availableFilters
            : [],
        ),
        columnType: column.columnType,
        useShortResult: column.useShortResult,
        foregroundColor: column.foregroundColor,
        backgroundColor: column.backgroundColor,
        renderHeader: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <CustomColumnHeader
              filterModel={filterModel}
              sortModel={sortModel}
              availableFilters={column.availableFilters}
              headerName={column.headerName}
              field={column.field}
              allColumns={columns}
              onToggleVisibility={toggleColumnVisibility}
              setPinnedColumns={setPinnedColumns}
              pinnedColumns={pinnedColumns}
              columnType={column.columnType}
              onOpenFilter={onOpenFilter}
              onSort={handleSort}
              sortable={column.field === "createdAt"}
            />
          </div>
        ),
      };
    },
  ) as any[];

  let actionsDataTable = actions?.map((action: any) => {
    const createdAt = moment(action?.createdAt);
    createdAt.locale(locale);
    const createdAtModify = createdAt.format("l, LT");

    const device = () => {
      if (!action.deviceName) {
        return t("Data.table.No_device");
      }
      if (!action.deviceName) {
        return t("Fleet_users_devices.table.Empty_device");
      } else {
        return action.deviceName;
      }
    };

    const location = () => {
      if (!action.geoPointName) {
        return t("Data.table.No_geo_point");
      } else {
        return action.geoPointName;
      }
    };

    const exportedMeasure = action?.aggExportedPredictions ?? {};
    const measures = action?.aggPayloads ?? {};
    const labels = action?.labels ?? {};

    // build row generically based on fetched columns
    const row =
      columnsDataTable?.reduce((obj: any, col) => {
        if (col.field === "orgDeviceId") {
          obj[col.field] = device();
        } else if (col.field === "billedCredits") {
          obj[col.field] =
            action.billedCredits != null ? action.billedCredits / 1000 : 0;
        } else if (col.field === "cropTextId") {
          obj[col.field] = action.cropTextId
            ? action.cropTextId.text
            : t("Data.table.No_crop");
        } else if (col.field === "scenarioNameTextId") {
          obj[col.field] = action.scenarioNameTextId
            ? action.scenarioNameTextId.text
            : "";
        } else if (col.field === "geoPointName") {
          obj[col.field] = location();
        } else if (col.field === "createdAt") {
          obj[col.field] = createdAtModify;
        } else if (col.field === "actionId") {
          // do nothing
        } else if (col.columnType === "exported-measure") {
          obj[col.field] = exportedMeasure[col.field];
        } else if (col.columnType === "label") {
          obj[col.field] = labels[col.field];
        } else if (col.columnType === "measure") {
          obj[col.field] = measures[col.field]?.predictionsV2;
        } else {
          obj[col.field] = action[col.field];
        }

        return obj;
      }, {}) || {};

    row["colors"] = action.cropInfoColor;

    row["id"] = action?.pastActionId;
    row["details"] = action.pastActionId;
    row["status"] = "inDevelopment";

    return row;
  });

  if (columnsDataTable?.length === 0 || !columnsDataTable) {
    columnsDataTable = [];
  }

  const columnSwitchesValues =
    columnsDataTable[0] !== undefined
      ? columnsDataTable?.map((col: any) => ({
          name: col.headerName ?? col.field,
          id: col.field,
          isSelected: col.show,
        }))
      : [
          {
            name: "",
            id: "",
            isSelected: false,
          },
        ];

  return {
    columnsDataTable,
    columnSwitchesValues,
    actionsDataTable,
  };
};

export default DataTableColumns;
